import { useCallback } from 'react';
import { Modal } from '@tanium/react-modal';
import { Stack } from '@tanium/react-stack';
import { Form, Formik } from 'formik';
import { Button } from '@tanium/react-button';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { MsEntraIDGroupTargetAccess } from 'gql/MsEntraIDGroupTargetAccess';
import Helper from 'utils/Helper';
import FormTextField from '../form/formTextField';

type MsEntraIDGroupTargetAccessForm = {
  adGroups: string;
  roleName: string;
  description: string;
};

const defaultCreateForm: MsEntraIDGroupTargetAccessForm = {
  adGroups: '',
  roleName: '',
  description: '',
};

function mapMsEntraIDGroupTargetAccessToForm({
  adGroups,
  roleName,
  description,
}: MsEntraIDGroupTargetAccess): MsEntraIDGroupTargetAccessForm {
  return {
    adGroups: adGroups.join(','),
    roleName,
    description,
  };
}

export type Operation =
  | {
      type: 'create';
      adGroups: string[];
      roleName: string;
      description: string;
      targetOrTargetGroupId: string;
    }
  | {
      type: 'update';
      adGroups: string[];
      roleName: string;
      description: string;
      id: string;
      targetOrTargetGroupId: string;
    }
  | {
      type: 'cancel';
    };

type MsEntraIDGroupTargetAccessCRUDModalProps = {
  isOpen: boolean;
  onProcess: (operation: Operation) => void;
  msEntraIDGroupTargetAccessToEdit?: MsEntraIDGroupTargetAccess;
  targetOrTargetGroupId: string;
};

const MsEntraIDGroupTargetAccessCRUDModal: React.VFC<MsEntraIDGroupTargetAccessCRUDModalProps> = ({
  onProcess,
  msEntraIDGroupTargetAccessToEdit,
  targetOrTargetGroupId,
  isOpen,
}) => {
  const { t } = useTranslation();

  const handleSubmit = useCallback(
    (data: MsEntraIDGroupTargetAccessForm) => {
      const operation: Operation =
        msEntraIDGroupTargetAccessToEdit === undefined
          ? {
              type: 'create',
              adGroups: Helper.safeConvertOptionalStringToArray(data.adGroups),
              roleName: data.roleName,
              description: data.description,
              targetOrTargetGroupId,
            }
          : {
              type: 'update',
              adGroups: Helper.safeConvertOptionalStringToArray(data.adGroups),
              roleName: data.roleName,
              description: data.description,
              id: msEntraIDGroupTargetAccessToEdit.id,
              targetOrTargetGroupId: msEntraIDGroupTargetAccessToEdit.targetOrTargetGroupID,
            };
      onProcess(operation);
    },
    [onProcess, msEntraIDGroupTargetAccessToEdit, targetOrTargetGroupId]
  );

  const handleCancel = useCallback(() => {
    onProcess({
      type: 'cancel',
    });
  }, [onProcess]);

  const validationSchema = yup.object().shape({
    roleName: yup.string().label(t('MS_ENTRA_ID_GROUP_TARGET_ACCESS_GROUP_NAME_LABEL')).required(),
    description: yup.string().label(t('DESCRIPTION')).required(),
    adGroups: yup.string().label(t('AD_GROUPS')).required(),
  });

  return (
    <Modal
      isOpen={isOpen}
      size="md"
      onRequestClose={handleCancel}
      header={
        msEntraIDGroupTargetAccessToEdit === undefined
          ? t('MS_ENTRA_ID_GROUP_TARGET_ACCESS_CREATE_MODAL_HEADER')
          : t('MS_ENTRA_ID_GROUP_TARGET_ACCESS_UPDATE_MODAL_HEADER')
      }
    >
      <Formik
        initialValues={
          msEntraIDGroupTargetAccessToEdit !== undefined
            ? mapMsEntraIDGroupTargetAccessToForm(msEntraIDGroupTargetAccessToEdit)
            : defaultCreateForm
        }
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form>
          <Stack direction="column" itemSpacing={1}>
            <FormTextField
              label={t('MS_ENTRA_ID_GROUP_TARGET_ACCESS_GROUP_NAME_LABEL')}
              description={t('MS_ENTRA_ID_GROUP_TARGET_ACCESS_GROUP_NAME_DESCRIPTION')}
              fieldName="roleName"
              placeholder={t('MS_ENTRA_ID_GROUP_TARGET_ACCESS_GROUP_NAME_PLACEHOLDER')}
              required
            />
            <FormTextField
              label={t('DESCRIPTION')}
              description={t('MS_ENTRA_ID_GROUP_TARGET_ACCESS_DESCRIPTION_DESCRIPTION')}
              fieldName="description"
              placeholder={t('MS_ENTRA_ID_GROUP_TARGET_ACCESS_DESCRIPTION_PLACEHOLDER')}
              required
            />
            <FormTextField
              label={t('AD_GROUPS')}
              description={t('AD_GROUPS_DESCRIPTION')}
              fieldName="adGroups"
              placeholder={t('AD_GROUPS_PLACEHOLDER')}
              required
            />
            <Stack itemSpacing={1}>
              <Button data-testid="submit" type="submit">
                {msEntraIDGroupTargetAccessToEdit === undefined ? t('CREATE') : t('UPDATE')}
              </Button>
              <Button type="button" data-testid="cancel" variant="secondary" onClick={handleCancel}>
                {t('CANCEL')}
              </Button>
            </Stack>
          </Stack>
        </Form>
      </Formik>
    </Modal>
  );
};

export default MsEntraIDGroupTargetAccessCRUDModal;
